import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, TextField, Table, TableHead, TableBody, TableCell, Tabs, Tab } from '@mui/material';
import { primaryColor } from '../../../../../../utils/theme';

export const SummaryCardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
}));

export const SummaryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  height: '100%',
}));

export const SummaryIconWrapper = styled(Box)<{ colorName: 'primary' | 'info' | 'error' }>(({ theme, colorName }) => ({
  backgroundColor: alpha(theme.palette[colorName]?.main || theme.palette.grey[300], 0.1),
  borderRadius: '8px',
  padding: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '2rem',
    color: theme.palette[colorName]?.main || theme.palette.grey[600],
  },
}));

export const SummaryContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.grey[600],
  marginBottom: theme.spacing(0.25),
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme.palette.custom.sidebarText,
  lineHeight: 1,
  marginBottom: theme.spacing(0.25),
}));

export const SummaryDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.grey[500],
}));

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
}));

export const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  width: '100%',
  flexWrap: 'wrap',
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  width: '400px',
  '& .MuiOutlinedInput-root': {
    height: '36px', 
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.white.main,
    fontSize: '0.85rem',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
      borderWidth: '1px',
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.grey[500],
      '& svg': { fontSize: '18px' }
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  }
}));

export const StatusTabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  marginBottom: theme.spacing(3),
}));

export const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
});

export const StatusTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.grey[600],
  padding: theme.spacing(1, 2),
  minHeight: '48px',
  '&.Mui-selected': {
    color: primaryColor,
  },
}));

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,

  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText,
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main, 
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.primary.light, 
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
  whiteSpace: 'nowrap',
}));

interface StatusTagProps {
  status: string;
}

export const StatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  const normalizedStatus = status?.toLowerCase();

  switch (normalizedStatus) {
    case 'active':
    case 'approved':
      bgColor = alpha(primaryColor, 0.15);
      color = primaryColor;
      break;
    case 'pending':
      bgColor = alpha(theme.palette.warning.light, 0.5);
      color = theme.palette.warning.dark;
      break;
    case 'suspended':
    case 'blocked':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.error.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    width: 'fit-content',
    textTransform: 'sentence case',
  };
});

export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));

export const WorkerListContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.palette.custom.borderRadius,
}));
